import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import type { PlayerInjuryColor } from '#src/components/player/InjuryTable/index.tsx';
import { InjuryTable } from '#src/components/player/InjuryTable/index.tsx';

import type { InjuryTagTemplate_injury } from './__generated__/index.graphql.ts';

const StyledChip = styled(Chip)`
  &.red {
    background-color: var(--c-red-400);
  }
  &.yellow {
    background-color: var(--c-yellow-400);
    color: var(--c-black);
  }
`;

type Props = {
  injury?: InjuryTagTemplate_injury | null;
  getPlayerInjuryColor: (status: string | null) => PlayerInjuryColor;
  getPlayerInjuryMessage: (
    status: string | null,
    options?: { long?: boolean }
  ) => MessageDescriptor;
  size: ComponentProps<typeof Chip>['size'];
};

export const InjuryTagTemplate = ({
  injury,
  getPlayerInjuryColor,
  getPlayerInjuryMessage,
  size,
}: Props) => {
  if (!injury) return null;
  const { status } = injury;
  const bgColor = getPlayerInjuryColor(status);

  return (
    <Tooltip
      title={
        <InjuryTable
          injury={injury}
          abbreviate
          getPlayerInjuryColor={getPlayerInjuryColor}
          getPlayerInjuryMessage={getPlayerInjuryMessage}
        />
      }
    >
      <StyledChip size={size} className={classNames(bgColor)}>
        <FormattedMessage {...getPlayerInjuryMessage(status)} />
      </StyledChip>
    </Tooltip>
  );
};

InjuryTagTemplate.fragments = {
  injury: gql`
    fragment InjuryTagTemplate_injury on Injury {
      id
      status
      ...InjuryTable_injury
    }
    ${InjuryTable.fragments.injury}
  ` as TypedDocumentNode<InjuryTagTemplate_injury>,
};
